// Read-only strategy summary for the wizard's validation step.
//
// The server picks the recommended strategy (A/B/C/D) from the validation
// report; the user can override before kicking off processing. We render
// the four options as a radio group with the recommended one pre-selected.

import type { ImportStrategy } from '@ohear/shared'
import { Badge } from '@ohear/ui'

const STRATEGY_DESCRIPTIONS: Record<ImportStrategy, { title: string; body: string }> = {
  A: {
    title: 'Strategy A: Script + SRT',
    body: 'Highest fidelity. Cues align directly to SRT timecodes; line text from the script.',
  },
  B: {
    title: 'Strategy B: Script only',
    body: 'WhisperX produces word-aligned timecodes; cues fall back to script line text.',
  },
  C: {
    title: 'Strategy C: SRT only',
    body: 'Cues align to SRT timecodes; line text taken from the SRT itself.',
  },
  D: {
    title: 'Strategy D: Reference audio only',
    body: 'WhisperX transcribes from the ref audio. Slowest, lowest fidelity — review carefully.',
  },
}

export interface StrategyPickerProps {
  recommended: ImportStrategy
  value: ImportStrategy
  onChange: (next: ImportStrategy) => void
  /** Disable when processing has already kicked off. */
  disabled?: boolean
}

const ALL: ImportStrategy[] = ['A', 'B', 'C', 'D']

export function StrategyPicker({ recommended, value, onChange, disabled }: StrategyPickerProps) {
  return (
    <fieldset className="space-y-2" disabled={disabled}>
      <legend className="mb-2 text-[14px] font-medium text-[var(--text)]">Import strategy</legend>
      {ALL.map((s) => {
        const meta = STRATEGY_DESCRIPTIONS[s]
        const checked = value === s
        return (
          <label
            key={s}
            className={`flex cursor-pointer items-start gap-3 rounded-[var(--radius)] border p-3 ${
              checked
                ? 'border-[var(--accent)] bg-[var(--bg-3)]'
                : 'border-[var(--border)] bg-[var(--bg-2)]'
            }`}
          >
            <input
              type="radio"
              name="import-strategy"
              value={s}
              checked={checked}
              onChange={() => onChange(s)}
              className="mt-1"
            />
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-[13px] font-medium text-[var(--text)]">{meta.title}</span>
                {s === recommended ? <Badge variant="green">Recommended</Badge> : null}
              </div>
              <p className="mt-1 text-[12px] text-[var(--text-2)]">{meta.body}</p>
            </div>
          </label>
        )
      })}
    </fieldset>
  )
}
